import React from 'react';
import { useJazyk } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useContactEmail } from '../hooks/useContactEmail';
import { useTextSettings } from '../hooks/useTextSettings';
import { SectionHeader } from '../components/SectionHeader';
import {
  Heart, ShieldCheck, Users, Lock, MessageCircle, HandHeart,
  ChevronRight, Scale, Phone
} from 'lucide-react';

// Jen klíče – překládá se až při vykreslení, kdy je znám jazyk.
const principles = [
  {
    icon: Heart,
    color: '#F5A9B8',
    klicTitulku: 'kom.respekt',
    klicTextu: 'kom.respektPopis',
  },
  {
    icon: ShieldCheck,
    color: '#5BCEFA',
    klicTitulku: 'kom.obtezovani',
    klicTextu: 'kom.obtezovaniPopis',
  },
  {
    icon: MessageCircle,
    color: '#8A7CFF',
    klicTitulku: 'kom.spam',
    klicTextu: 'kom.spamPopis',
  },
  {
    icon: Lock,
    color: '#A8E6CF',
    klicTitulku: 'kom.soukromi',
    klicTextu: 'kom.soukromiPopis',
  },
  {
    icon: HandHeart,
    color: '#F7C59F',
    klicTitulku: 'kom.pomahame',
    klicTextu: 'kom.pomahamePopis',
  },
  {
    icon: Users,
    color: '#C3AED6',
    klicTitulku: 'kom.prostor',
    klicTextu: 'kom.prostorPopis',
  },
];

export default function CommunityPage() {
  const { t } = useJazyk();
  const contactEmail = useContactEmail();
  const texts = useTextSettings();
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3" id="guidelines">
        <div className="h-1 bg-gradient-to-r from-[#5BCEFA] via-[#F5A9B8] to-[#FFFFFF] rounded mx-auto w-24 mb-4" />
        <SectionHeader sectionKey="community" klicTitulku="kom.titulek" klicPodtitulku="kom.podtitulek" defaultColor="#8A7CFF" />
      </div>

      {/* Principles */}
      <div className="grid gap-4 sm:grid-cols-2">
        {principles.map((p) => {
          const Icon = p.icon;
          return (
            <Card key={p.klicTitulku} className="bg-white border-border/50 hover:shadow-sm transition-shadow">
              <CardContent className="p-5">
                <div className="flex gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${p.color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color: p.color }} />
                  </div>
                  <div>
                    <p className="font-semibold text-bloom-text text-sm mb-1">{t(p.klicTitulku)}</p>
                    <p className="text-xs text-bloom-sub leading-relaxed">{t(p.klicTextu)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Reporting */}
      <Card className="bg-bloom-violet/5 border-bloom-violet/20">
        <CardContent className="p-6 space-y-2">
          <p className="font-semibold text-bloom-text flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-bloom-violet" />{t('kom.jakNahlasit')}
          </p>
          <p className="text-sm text-bloom-sub leading-relaxed">
            {t('kom.jakNahlasitPopis')}
              <a href={`mailto:${contactEmail}`} className="text-bloom-violet hover:underline">{contactEmail}</a>}.
            {t('kom.duverne')}
          </p>
        </CardContent>
      </Card>

      {/* About */}
      <Card className="bg-white border-border/50" id="about">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-bloom-text">O projektu Bloom</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-bloom-sub leading-relaxed space-y-2">
          <p>{texts.about_text || t('kom.oProjektu')}</p>
          <p>Kontakt: {contactEmail && <a href={`mailto:${contactEmail}`} className="text-bloom-violet hover:underline">{contactEmail}</a>}</p>
        </CardContent>
      </Card>

      {/* Privacy */}
      <Card className="bg-white border-border/50" id="privacy">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-bloom-text flex items-center gap-2">
            <Lock className="w-4 h-4 text-bloom-violet" />{t('kom.ochranaSoukromi')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-bloom-sub leading-relaxed space-y-2">
          <p>{t('kom.dataPopis')}</p>
          <p>{t('kom.dataTretiStrany')}</p>
          <p>{t('kom.dataSmazani')}</p>
        </CardContent>
      </Card>

      {/* Contact section */}
      <Card className="bg-white border-border/50" id="contact">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-bloom-text flex items-center gap-2">
            <Phone className="w-4 h-4 text-bloom-violet" />Kontakt
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-bloom-sub leading-relaxed space-y-2">
          <p>{texts.contact_text || t('kom.kontaktText')}</p>
          {contactEmail && (
            <p><a href={`mailto:${contactEmail}`} className="text-bloom-violet hover:underline font-medium text-base">{contactEmail}</a></p>
          )}
        </CardContent>
      </Card>

      {/* Footer nav */}
      <div className="border-t border-border pt-6 flex flex-wrap gap-x-6 gap-y-2 justify-center text-xs text-bloom-sub">
        <Link to="/" className="hover:text-bloom-violet transition-colors">{t('kom.zpet')}</Link>
        <a href={contactEmail ? `mailto:${contactEmail}` : '#'} className="hover:text-bloom-violet transition-colors flex items-center gap-1">
          <Phone className="w-3 h-3" />Kontakt
        </a>
      </div>
    </div>
  );
}
